import { Empty } from 'antd';
import { InverterApi } from 'api/InverterApi';
import { plantApi } from 'api/plantApi';
import { weatherApi } from 'api/weatherApi';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import { selectPlantItemSidebar } from 'components/Common/Sidebar/sidebarSlice';
import ChartActivePower from 'features/Plant/components/ChartActivePower/ChartActivePower';
import { plantActions, selectPlantInfo } from 'features/Plant/plantSlice';
import {
  inverterInfo,
  objectAccrual,
  objectWeather,
  ResponseData,
  responseDataReport,
} from 'models';
import moment from 'moment';
import * as React from 'react';
import { customAccrualChart, customDataTemperatureChart, formatArray } from 'utils/common';
import AccrualChart from '../AccrualChart/AccrualChart';
import TemperatureChart from '../TemperatureChart/TemperatureChart';
import style from './ContentReport.module.scss';
export interface ContentReportProps {
  isExport: boolean;
  dateTime: string;
}

export default function ContentReport(props: ContentReportProps) {
  const { isExport, dateTime } = props;
  const dispatch = useAppDispatch();
  const plantItemSelected = useAppSelector(selectPlantItemSidebar);
  const itemPlant = useAppSelector(selectPlantInfo);
  const [weatherInfo, setWeatherInfo] = React.useState<inverterInfo>();
  const [newDataChartPAA, setNewDataChartPAA] = React.useState<responseDataReport>();
  const [valueIrra, setValueIrra] = React.useState<number | null>(null);
  const [heightPower, setHeightPower] = React.useState<number | null>(null);
  const [energy, setEnergy] = React.useState<number>(0);
  const [estimated, setEstimated] = React.useState<number>(0);
  const [tomorrow, setTomorrow] = React.useState<number>(0);
  const [planMonth, setPlanMonth] = React.useState<number | null>(null);
  const [accumulated, setAccumulated] = React.useState<number | null>(null);
  const [objectAccrual, setObjectAccrual] = React.useState<objectAccrual>();

  React.useEffect(() => {
    if (plantItemSelected) {
      (async () => {
        await dispatch(plantActions.fetchPlantInfo({ type: 'plant_id', id: plantItemSelected }));
        const res: inverterInfo = await weatherApi.fetchWeatherStation(plantItemSelected);
        setWeatherInfo(res);
      })();
    }
  }, [plantItemSelected, dispatch]);

  React.useEffect(() => {
    if (plantItemSelected && weatherInfo) {
      fetchDataChart(plantItemSelected, weatherInfo.id, dateTime);
    }
  }, [plantItemSelected, weatherInfo, dateTime]);

  const formatDataChart = (array: ResponseData[]) => {
    let newList: ResponseData[] = [];
    array.map((item) => {
      if (item.value !== null) {
        let newObject = {
          ts: item.ts,
          value: parseFloat((item.value / 1000).toFixed(3)),
        };
        return (newList = [...newList, newObject]);
      } else {
        return (newList = [...newList, item]);
      }
    });
    return newList;
  };

  const fetchDataChart = async (plantId: string, weatherId: string, date: string) => {
    const response: ResponseData[] = await plantApi.fetchPropertiesPlant({
      type: 'output-power/avg-per-ten-mins',
      id: plantId,
      time: date,
    });
    const outPut_power = await formatDataChart(response);
    setHeightPower(findHeightPower(outPut_power));
    const resOne: objectWeather = await InverterApi.fetchDataInverterGraph({
      id: weatherId,
      text: ['irra', 'temp'],
      start_ts: moment(date).valueOf(),
      end_ts: moment(moment(moment(date).add(1, 'days')).format('YYYY-MM-DD')).valueOf(),
      duration: 600000,
    });
    setValueIrra(
      sumValue(
        Object.keys(resOne).findIndex((item) => item === 'irra') !== -1 ? resOne['irra'] : []
      )
    );
    let newDA = await customDataTemperatureChart(
      outPut_power,
      Object.keys(resOne).findIndex((item) => item === 'irra') !== -1
        ? customDataChart(resOne['irra'])
        : [],
      Object.keys(resOne).findIndex((item) => item === 'temp') !== -1
        ? customDataChart(resOne['temp'])
        : []
    );
    setNewDataChartPAA(newDA);
    const responseOne: ResponseData[] = await plantApi.fetchInfoPropertiesEnergy({
      type: 'output-energy',
      properties: 'month',
      id: plantId,
      time: moment(date).format('YYYY-MM'),
    });
    setEnergy(getOutPut(responseOne, moment(date).valueOf()));
    setAccumulated(sumValue(responseOne));
    const responseTwo: ResponseData[] = await plantApi.fetchInfoPropertiesEnergy({
      type: 'estimated-output-energy',
      id: plantId,
      time: moment(date).format('YYYY-MM'),
      properties: 'month',
    });
    setEstimated(getOutPut(responseTwo, moment(date).valueOf()));
    setTomorrow(
      getOutPut(
        responseTwo,
        moment(moment(moment(date).add(1, 'days')).format('YYYY-MM-DD')).valueOf()
      )
    );
    setPlanMonth(sumValue(responseTwo));
    const responseThree: ResponseData[] = await plantApi.fetchInfoPropertiesEnergy({
      type: 'output-energy',
      properties: 'year',
      id: plantId,
      time: moment(date).format('YYYY'),
    });
    const responseFour: ResponseData[] = await plantApi.fetchInfoPropertiesEnergy({
      type: 'estimated-output-energy',
      id: plantId,
      time: moment(date).format('YYYY'),
      properties: 'year',
    });
    setObjectAccrual(customAccrualChart(formatArray(responseFour), formatArray(responseThree)));
  };

  const getOutPut = (array: ResponseData[], date: number) => {
    let newValue = 0;
    array.forEach((i) => {
      if (i.ts === date) {
        var output_value = i.value ? i.value / 1000 : 0;
        newValue = output_value;
      }
    });
    return newValue;
  };

  const percentEnergy = (energy: number, estimated: number) => {
    return (energy / estimated) * 100;
  };

  const performanceCalculation = (energy: number, system: number) => {
    return (energy / system) * 100;
  };

  const sumValue = (array: ResponseData[]): number => {
    let sum = 0;
    array.forEach((item) => {
      if (item.value !== null) sum += item.value;
    });
    return parseFloat((sum / 1000).toFixed(3));
  };

  const findHeightPower = (array: ResponseData[]): number | null => {
    if (array.length === 0) return null;
    let newArray: number[] = [];
    array.forEach((item) => {
      if (item.value !== null) {
        newArray = [...newArray, item.value];
      }
    });
    let sum = newArray.sort((a, b) => b - a);
    return sum[0];
  };

  const customDataChart = (array: ResponseData[]) => {
    let newList: ResponseData[] = [];
    array.map((item) => {
      let newObject = {
        ts: Math.floor(item.ts / 600000) * 600000,
        value: item.value,
      };
      return (newList = [...newList, newObject]);
    });
    return newList;
  };

  return (
    <>
      <div
        className={clsx(style.container_pdf, {
          [style.container_pdf_export]: isExport,
        })}
      >
        <div
          className={clsx(style.header, {
            [style.header_export]: isExport,
          })}
        >
          <div
            className={clsx(style.left_header, {
              [style.left_header_export]: isExport,
            })}
          >
            CÔNG TY TNHH KỸ THUẬT POSO
          </div>
          <div
            className={clsx(style.right_header, {
              [style.right_header_export]: isExport,
            })}
          >
            {itemPlant?.address}, ngày {moment(dateTime).format('DD')} tháng{' '}
            {moment(dateTime).format('MM')} năm {moment(dateTime).format('YYYY')}
          </div>
        </div>
        <div
          className={clsx(style.label_report, {
            [style.label_report_export]: isExport,
          })}
        >
          BÁO CÁO NGÀY & KẾ HOẠCH NGÀY TIẾP THEO
        </div>
        <div
          className={clsx(style.label_info, {
            [style.label_info_export]: isExport,
          })}
        >
          I. THÔNG TIN TRONG NGÀY
        </div>
        <div
          className={clsx(style.label_info_default, {
            [style.label_info_default_export]: isExport,
          })}
        >
          1. Thông số thời tiết
        </div>
        <div
          className={clsx(style.temperature_chart, {
            [style.temperature_chart_export]: isExport,
          })}
        >
          {newDataChartPAA?.value === 'ok' ? (
            <TemperatureChart isExport={isExport} dataTemperatureChart={newDataChartPAA} />
          ) : (
            <Empty />
          )}
        </div>
        <div
          className={clsx(style.label_info_default, {
            [style.label_info_default_export]: isExport,
          })}
        >
          2. Thông số vận hành
        </div>
        <div
          className={clsx(style.label_info_default, {
            [style.label_info_default_export]: isExport,
          })}
        >
          2.1 Tổng bức xạ:
          <span>
            {valueIrra} kWh/m<sup>2</sup>
          </span>
        </div>
        <div
          className={clsx(style.label_info_default, {
            [style.label_info_default_export]: isExport,
          })}
        >
          2.2 Hiệu suất:
          <span>
            {energy !== 0
              ? `${percentEnergy(energy, estimated).toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })} %`
              : ''}
          </span>
        </div>
        <div
          className={clsx(style.label_info_default, {
            [style.label_info_default_export]: isExport,
          })}
        >
          2.3 Công suất cực đại:
          <span>{heightPower} kW</span>
        </div>
        <div
          className={clsx(style.label_info, {
            [style.label_info_export]: isExport,
          })}
        >
          II. CÔNG TÁC SẢN XUẤT
        </div>
        <div
          className={clsx(style.label_info_default, {
            [style.label_info_default_export]: isExport,
          })}
        >
          1. Sản lượng và doanh thu sản xuất điện trong ngày:
        </div>
        <div
          className={clsx(style.table_default, {
            [style.table_default_export]: isExport,
          })}
        >
          <div className={style.header_table}>
            <div
              className={clsx(style.label_col_one, {
                [style.label_col_one_export]: isExport,
              })}
              style={{
                width: '5%',
              }}
            >
              TT
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '30%',
              }}
            >
              Nội dung
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '10%',
              }}
            >
              Đơn vị
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '15%',
              }}
            >
              Kế hoạch trong ngày
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '10%',
              }}
            >
              Thực hiện
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '10%',
              }}
            >
              Đánh giá
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '20%',
              }}
            >
              Kết hoạch ngày tiếp theo
            </div>
          </div>
          <div className={style.container_table}>
            <div
              className={clsx(style.label_col_one, {
                [style.label_col_one_export]: isExport,
              })}
              style={{
                width: '5%',
              }}
            >
              -
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                paddingLeft: '10px',
                justifyContent: 'flex-start',
                width: '30%',
              }}
            >
              Sản lượng điện thương phẩm
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '10%',
              }}
            >
              KWh
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '15%',
              }}
            >
              {estimated.toFixed(2)}
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '10%',
              }}
            >
              {energy}
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '10%',
              }}
            >
              {energy !== 0
                ? `${percentEnergy(energy, estimated).toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })} %`
                : ''}
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '20%',
              }}
            >
              {tomorrow.toFixed(2)}
            </div>
          </div>
        </div>
        <div
          className={clsx(style.label_info_default, {
            [style.label_info_default_export]: isExport,
          })}
        >
          2. Lũy kế sản lượng điện trong tháng:
        </div>
        <div
          className={clsx(style.table_default, {
            [style.table_default_export]: isExport,
          })}
        >
          <div className={style.header_table}>
            <div
              className={clsx(style.label_col_one, {
                [style.label_col_one_export]: isExport,
              })}
              style={{
                width: '5%',
              }}
            >
              TT
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '30%',
              }}
            >
              Nội dung
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '10%',
              }}
            >
              Đơn vị
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '15%',
              }}
            >
              Kế hoạch tháng {moment(dateTime).format('MM')}
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '10%',
              }}
            >
              Lũy kế
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '30%',
              }}
            >
              Đánh giá (% so với kế hoạch đặt ra)
            </div>
          </div>
          <div className={style.container_table}>
            <div
              className={clsx(style.label_col_one, {
                [style.label_col_one_export]: isExport,
              })}
              style={{
                width: '5%',
              }}
            >
              -
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                paddingLeft: '10px',
                justifyContent: 'start',
                width: '30%',
              }}
            >
              Lũy kế sản lượng
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '10%',
              }}
            >
              KWh
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '15%',
              }}
            >
              {planMonth}
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '10%',
              }}
            >
              {accumulated}
            </div>
            <div
              className={clsx(style.label_col, {
                [style.label_col_export]: isExport,
              })}
              style={{
                width: '30%',
              }}
            >
              {accumulated !== 0 && accumulated && planMonth
                ? `${percentEnergy(accumulated, planMonth).toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })} %`
                : ''}
            </div>
          </div>
        </div>
        <div
          className={clsx(style.label_info_default, {
            [style.label_info_default_export]: isExport,
          })}
        >
          3. Biểu đồ tổng hợp lũy kế sản lượng năm:
        </div>
        <div
          className={clsx(style.temperature_chart, {
            [style.temperature_chart_export]: isExport,
          })}
        >
          {objectAccrual ? (
            <AccrualChart isExport={isExport} dataChart={objectAccrual} />
          ) : (
            <Empty />
          )}
        </div>
        <div
          className={clsx(style.label_info_default, {
            [style.label_info_default_export]: isExport,
          })}
        >
          4. Công suất, bức xạ:
        </div>
        <div
          className={clsx(style.temperature_chart, {
            [style.temperature_chart_export]: isExport,
          })}
        >
          {newDataChartPAA ? (
            <ChartActivePower
              isExport={isExport}
              isReport={true}
              newDataChartPAA={newDataChartPAA}
            />
          ) : (
            <Empty />
          )}
        </div>
      </div>
    </>
  );
}
